import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface UserProfileFooterProps {
  /** User display name */
  name: string;
  /** User email (optional) */
  email?: string | null;
  /** Callback when pressed (navigate to profile/settings) */
  onPress?: () => void;
  /** Whether sidebar is collapsed */
  collapsed?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * UserProfileFooter - Sidebar 底部用户信息区
 *
 * ChatGPT 风格设计：
 * - 显示用户头像（首字母）、名字、邮箱
 * - 点击进入个人中心/设置
 * - 固定在底部
 * - 紫色背景头像（与 App 端统一）
 */
export function UserProfileFooter({
  name,
  email,
  onPress,
  collapsed = false,
  className,
}: UserProfileFooterProps) {
  // Get first letter for avatar
  const initial = name?.charAt(0)?.toUpperCase() || 'U';

  const content = (
    <div className={cn('flex w-full items-center gap-3', collapsed && 'justify-center')}>
      {/* User Avatar (letter-based, purple background) */}
      <Avatar className="h-9 w-9 shrink-0 bg-violet-500">
        <AvatarFallback className="bg-violet-500 text-sm font-semibold text-white">
          {initial}
        </AvatarFallback>
      </Avatar>

      {/* User Info - only show when not collapsed */}
      {!collapsed && (
        <>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{name}</p>
            {email && <p className="truncate text-xs text-gray-500">{email}</p>}
          </div>

          {/* More icon - 靠右对齐 */}
          <MoreHorizontal className="h-4 w-4 shrink-0 text-gray-400" />
        </>
      )}
    </div>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onPress}
            className={cn(
              'flex w-full justify-center border-t border-gray-100 py-4 transition-colors hover:bg-gray-50',
              className
            )}
          >
            {content}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <div>
            <p className="font-medium">{name}</p>
            {email && <p className="text-xs text-gray-400">{email}</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (onPress) {
    return (
      <button
        onClick={onPress}
        className={cn(
          'flex w-full items-center gap-3 border-t border-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-50',
          className
        )}
      >
        {content}
      </button>
    );
  }

  return <div className={cn('border-t border-gray-100 px-4 py-3', className)}>{content}</div>;
}
